import { createElement, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";

import {
  useDynamicFormsFetch,
  useModalConfirm,
  useYupValidationResolver,
} from "@ryde/redux";
import {
  API_KEY_ENTITY,
  ApiKeyConst,
  ApiKeysApi,
  CompanyApi,
  DrawerType,
  errors,
  toApiKeyCompanySelectItems,
  toApiKeyRoleSelectItems,
  toCreateApiKeyDto,
  useDrawers,
  warnAndToastException,
  warnAndToastSimpleResult,
  type IRoleDropdownItem,
  type ISelectListItem,
} from "@ryde/shared";

import {
  ApiKeyCreateForm,
  apiKeyCreateValidationSchema,
  type IApiKeyCreateForm,
} from "./api-key-create-drawer.models";
import { ApiKeyTokenDialog } from "./api-key-token-dialog";

/**
 * Drives the "New API key" drawer. The company is a field of the key, never the active company in
 * the header. The created token is shown once in a dialog and is never logged or kept in state.
 */
export const useApiKeyCreateDrawer = () => {
  const { t } = useTranslation();
  const { closeDrawer } = useDrawers();
  const { confirmDialogResult } = useModalConfirm();
  const { fetchTableRows } = useDynamicFormsFetch(API_KEY_ENTITY);
  const resolver = useYupValidationResolver(
    apiKeyCreateValidationSchema,
    API_KEY_ENTITY,
  );

  const [companyItems, setCompanyItems] = useState<ISelectListItem[]>([]);
  const [roles, setRoles] = useState<IRoleDropdownItem[]>([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(true);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  // Only the newest roles request may write state, so switching companies quickly cannot let an
  // older company's roles land on the newer one.
  const rolesRequestIdRef = useRef(0);

  const methods = useForm<IApiKeyCreateForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: {
      [ApiKeyCreateForm.name]: "",
      [ApiKeyCreateForm.ttlDays]: ApiKeyConst.DEFAULT_TTL_DAYS,
    },
    resolver,
  });
  const {
    formState: { isValid },
    getValues,
    resetField,
    watch,
  } = methods;
  const companyId = watch(ApiKeyCreateForm.companyId);

  useEffect(() => {
    const loadCompanies = async (): Promise<void> => {
      try {
        const companies = await CompanyApi.getAll();
        setCompanyItems(toApiKeyCompanySelectItems(companies ?? []));
      } catch (error) {
        warnAndToastException({
          error,
          fallbackTranslationKey: errors.common.technicalProblemOccurred,
          translationFunction: t,
        });
      } finally {
        setIsLoadingCompanies(false);
      }
    };

    void loadCompanies();
  }, [t]);

  useEffect(() => {
    const requestId = rolesRequestIdRef.current + 1;
    rolesRequestIdRef.current = requestId;
    // roles are per company, so a role picked for the previous company no longer applies
    resetField(ApiKeyCreateForm.roleId);
    setRoles([]);

    if (!companyId) {
      setIsLoadingRoles(false);
      return;
    }

    const loadRoles = async (): Promise<void> => {
      setIsLoadingRoles(true);
      try {
        const result = await ApiKeysApi.getRoles(Number(companyId));

        if (requestId !== rolesRequestIdRef.current) {
          return;
        }

        if (!result.success || !result.data) {
          warnAndToastSimpleResult(result, t);
          return;
        }

        setRoles(result.data);
      } finally {
        if (requestId === rolesRequestIdRef.current) {
          setIsLoadingRoles(false);
        }
      }
    };

    void loadRoles();
  }, [companyId, resetField, t]);

  const roleItems = useMemo(
    () => toApiKeyRoleSelectItems({ roles, globalSuffix: t("common.global") }),
    [roles, t],
  );
  const isCreateDisabled =
    !isValid || isCreating || isLoadingCompanies || isLoadingRoles;

  const onCreateClick = useEvent(async (): Promise<void> => {
    setIsCreating(true);
    try {
      const result = await ApiKeysApi.create(toCreateApiKeyDto(getValues()));

      if (!result.success || !result.data) {
        warnAndToastSimpleResult(result, t);
        return;
      }

      const { token } = result.data;

      closeDrawer(DrawerType.ApiKeyCreate);
      fetchTableRows();
      void confirmDialogResult({
        title: t("apiKey.tokenDialog.title"),
        message: t("apiKey.tokenDialog.warning"),
        customContent: createElement(ApiKeyTokenDialog, { token }),
        showOkButton: false,
        cancelButtonTitle: t("apiKey.tokenDialog.done"),
      });
    } finally {
      setIsCreating(false);
    }
  });

  return {
    methods,
    companyId,
    companyItems,
    roleItems,
    isLoadingCompanies,
    isLoadingRoles,
    isCreating,
    isCreateDisabled,
    onCreateClick,
  };
};
